import React from 'react';
import type { ISODateRange } from 'shared/src/date-string-utils/types';
import { useCubeQuery } from '@/components/display/use-cube-query';
import { useLineItemContext } from '@/features/line-items/line-items-context';
import { PacingProgressBar } from './pacing-progress-bar';
import { MetricSkeleton } from '@/components/feedback/metric-skeleton';
import { getDateRangeState } from './metric-date-utils';

type UnitsProgressMetricProps = {
  dateRange: ISODateRange;
};

export function UnitsProgressMetric({ dateRange }: UnitsProgressMetricProps) {
  const { lineItem } = useLineItemContext();
  if (getDateRangeState(dateRange) === 'expired') return null;
  return <UnitsProgressMetricInner lineItemId={lineItem.id} dateRange={dateRange} />;
}

function UnitsProgressMetricInner({
  lineItemId,
  dateRange
}: {
  lineItemId: string;
  dateRange: ISODateRange;
}) {
  const result = useCubeQuery({
    dimensions: ['line_items.unit_price_type_name'],
    measures: [
      'line_item_metrics_daily.delivery_pacing',
      'line_item_metrics_daily.actual_units',
      'line_item_metrics_daily.planned_units',
      'line_item_metrics_daily.remaining_units'
    ],
    timeDimensions: [
      {
        dimension: 'line_item_metrics_daily.day',
        dateRange: [dateRange.from, dateRange.to]
      }
    ],
    filters: [
      {
        member: 'line_item_metrics_daily.line_item_id',
        operator: 'equals',
        values: [lineItemId]
      }
    ]
  });

  if (result.status === 'loading') return <MetricSkeleton />;
  if (result.status === 'error') return null;

  if (!result.rows[0] || !result.formattedRows[0]) {
    return <PacingProgressBar pacing="0" planned="0" remaining="0" pacingRaw={0} />;
  }

  const { 'line_item_metrics_daily.delivery_pacing': pacingRaw } = result.rows[0];
  const {
    'line_item_metrics_daily.delivery_pacing': pacing,
    'line_item_metrics_daily.planned_units': planned,
    'line_item_metrics_daily.remaining_units': remaining
  } = result.formattedRows[0];

  return (
    <PacingProgressBar
      pacing={pacing}
      planned={planned}
      remaining={remaining}
      pacingRaw={pacingRaw}
    />
  );
}
